import React, { useState, useEffect, useRef, useId } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ENTITIES } from './PolicyRepository';
import { useIsNarrow, gridCols } from './ui';
// DATEDAY: date-only values shown as their calendar day in every timezone.
import { toDisplayDate } from './dateOnly';

// Secondary Entity dropdown options - same canonical entity value set as primary_entity
// (PolicyRepository.js's ENTITIES / backend's COMPANY_TO_ENTITY values), minus the "All
// Entities" filter-only option which doesn't apply to a single employee's entity.
const SECONDARY_ENTITY_OPTIONS = ENTITIES.filter(e => e !== 'All Entities');

const API = '/api';
const isTestAccount = () => { try { return JSON.parse(localStorage.getItem('enko_auth'))?.is_test_account === true; } catch { return false; } };

// EMAILFORMAT: mirrors the backend's _is_valid_email_format pattern (employees.py) exactly -
// local part, single @, domain with at least one dot, no consecutive/leading/trailing dots.
// eslint-disable-next-line no-useless-escape
const EMAIL_FORMAT_RE = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)+$/;

const LOCATION_COMPANIES = {
  'UK': ['Enko Capital Management LLP', 'Other'],
  'South Africa': [
    'Enko Capital Investments Pty Ltd',
    'Enko Capital Managers Pty Ltd',
    'ECM SA Managers (Pty) Ltd',
    'Other',
  ],
  'Mauritius': ['Enko Fund Managers Ltd', 'Other'],
  "Côte d'Ivoire": ['Enko Capital West Africa', 'Other'],
  // SENEGALLOC: Senegal is a LIAISON OFFICE of the Côte d'Ivoire entity (Enko Capital West Africa
  // (CIV)), not a separate legal entity. It reuses the SAME company, so it resolves to the SAME
  // primary_entity via COMPANY_TO_ENTITY - which is exactly why the CIV entity now maps to TWO
  // locations (see ENTITY_TO_LOCATIONS below).
  'Senegal': ['Enko Capital West Africa', 'Other'],
  'Cameroon': ['Enko Capital Central Africa Ltd', 'Other'],
  'Rwanda': ['Enko Capital Rwanda Ltd', 'Other'],
};

// COMPANYLOCK (confirmed COO decision): the real (selectable) companies for a location are its
// options MINUS the "Other" catch-all - "Other" is never a genuine second choice. A location with
// exactly one real company (every location except South Africa, which has 3) must lock the Company
// field to that company; only a location with multiple real companies keeps it selectable. Pure UI
// helper over LOCATION_COMPANIES - it does NOT touch COMPANY_TO_ENTITY or the entity resolution.
const realCompaniesFor = (location) =>
  (LOCATION_COMPANIES[location] || []).filter(c => c !== 'Other');

// DELEGATELOCFILTER: same 8 company -> entity pairs as the backend's COMPANY_TO_ENTITY
// (employees.py) - kept as an exact mirror rather than imported, since this is a plain static
// table with no logic and no existing shared-constants channel between the two languages.
const COMPANY_TO_ENTITY = {
  'Enko Capital Management LLP':      'Enko Capital Management LLP (UK)',
  'Enko Capital Investments Pty Ltd': 'Enko Capital Investments Pty Ltd (SA)',
  'Enko Capital Managers Pty Ltd':    'Enko Capital Managers Pty Ltd (SA)',
  'ECM SA Managers (Pty) Ltd':        'ECM SA Managers (Pty) Ltd',
  'Enko Fund Managers Ltd':           'Enko Fund Managers Ltd (MU)',
  'Enko Capital West Africa':         'Enko Capital West Africa (CIV)',
  'Enko Capital Central Africa Ltd':  'Enko Capital Central Africa (CMR)',
  'Enko Capital Rwanda Ltd':          'Enko Capital Rwanda Ltd (RW)',
};

// Reverse-map primary_entity -> the location(s) a delegate in that entity may submit for, so a
// non-privileged delegate's own Employee.primary_entity (fetched via GET /employees/me) can
// pre-filter the Location dropdown. Composed from LOCATION_COMPANIES + the mirror above - built
// here, not fetched, since both are already the frontend's own source of truth for these dropdowns.
//
// SENEGALLOC: this is now entity -> ARRAY of locations (was one string). An entity USUALLY maps to
// one location, but the CIV entity maps to TWO - "Côte d'Ivoire" and its "Senegal" liaison office,
// which share the SAME company and therefore the SAME primary_entity. A CIV delegate (Ida) must be
// able to pick EITHER, so a single-value map (last-write-wins) was wrong. Every other entity still
// yields a one-element array, so their delegates behave exactly as before. (South Africa's 3
// companies map to 3 DIFFERENT entities, each a distinct one-location array - unaffected.)
export const ENTITY_TO_LOCATIONS = Object.entries(LOCATION_COMPANIES).reduce((acc, [location, companies]) => {
  companies
    .filter(c => c !== 'Other')
    .forEach(c => {
      const entity = COMPANY_TO_ENTITY[c];
      if (!entity) return;
      (acc[entity] = acc[entity] || []);
      if (!acc[entity].includes(location)) acc[entity].push(location);
    });
  return acc;
}, {});

const DEPARTMENTS = [
  'Senior Management',
  'Investments',
  'Operations',
  'Risk',
  'Compliance',
  'Marketing',
  'Legal',
  'Finance',
  'HR',
  'IT',
];

// GRADEFIELD: the 4 fixed seniority GRADES (a strict dropdown). Genuinely distinct from job_title
// (an actual role like "Portfolio Manager"): a person can hold both. Reverts the earlier JOBTITLESTD
// job-title dropdown, whose 4 options were really these grades - job_title is now free text.
// GRADEREMOVE (confirmed COO decision): the seniority Grade field is removed from the interface
// and from submission. The employees.grade COLUMN and its existing values are deliberately KEPT
// for historical purposes, and Employee.grade remains on the SQLAlchemy model so those rows stay
// readable - only the capture, the display and the API input are gone. Its one functional use,
// the Partner contract-upload waiver, was already replaced by the explicit contract_exempt field
// (CONTRACTEXEMPT2), so nothing depends on this value any more.

const DEMO = {
  first_name: 'Demo',
  last_name: 'Employee',
  company_email: 'demo.employee@example.com',
  personal_email: 'demo.personal@example.com',
  company: 'Enko Capital Management LLP',
  job_title: 'Portfolio Manager',
  // CONTRACTEXEMPT2: present here (like every other boolean field DEMO sets) because "Fill demo
  // data" REPLACES the whole form object via setForm(DEMO), not a merge - an omitted key would
  // leave the checkbox uncontrolled after a demo fill rather than defaulting to unticked.
  contract_exempt: false,
  department: 'Investments',
  start_date: '2025-01-15',
  location: 'UK',
  employee_type: 'employee',
  requires_visa: true,
  is_certified_function: false,
  is_non_certified_function: false,
  is_senior_management: false,
  classification_none_confirmed: true,
};

const RequiredMark = () => {
  const [show, setShow] = React.useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-block', marginLeft: '2px' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}>
      <span style={{ color: '#e74c3c', cursor: 'default' }}>*</span>
      {show && (
        <span style={{
          position: 'absolute', bottom: 'calc(100% + 6px)', left: '50%',
          transform: 'translateX(-50%)', backgroundColor: '#1a1a2e', color: '#fff',
          padding: '5px 10px', borderRadius: '4px', fontSize: '12px',
          whiteSpace: 'nowrap', zIndex: 200, pointerEvents: 'none',
          boxShadow: '0 2px 6px rgba(0,0,0,0.25)'
        }}>
          This field is required.
          <span style={{
            position: 'absolute', top: '100%', left: '50%',
            transform: 'translateX(-50%)', width: 0, height: 0,
            borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
            borderTop: '5px solid #1a1a2e'
          }} />
        </span>
      )}
    </span>
  );
};

const Spinner = () => (
  <span style={{
    display: 'inline-block', width: '13px', height: '13px',
    border: '2px solid rgba(255,255,255,0.35)',
    borderTopColor: '#fff', borderRadius: '50%',
    animation: 'spin 0.65s linear infinite',
    marginRight: '8px', verticalAlign: 'middle'
  }} />
);

const EMPTY_FORM = {
  first_name: '', last_name: '', company_email: '', personal_email: '', company: '',
  secondary_entity: '',
  job_title: '', contract_exempt: false, department: '', start_date: '',
  location: '', employee_type: '', intern_term: '', intern_duration: '', internship_end_date: '', requires_visa: false, gender: '', mobile: '',
  can_submit_new_starters: false,
  manager_id: '',
  existing_employee: false,
  is_certified_function: false,
  is_non_certified_function: false,
  is_senior_management: false,
  classification_none_confirmed: false,
};

function AddEmployee({ onClose, onCreated, inline = false, createdBy = null, currentUserRole = null }) {
  // STARTDATEMAILS: the start date message, translated, for the screen's check and the server's.
  const { t } = useTranslation();
  const startDateRequired = t('addEmployee.startDateRequired', 'Start date is required.');
  // RESPONSIVE Phase 2: two-column field rows (styles.row) reflow to one column below ~900px.
  const isNarrow = useIsNarrow();
  const rowStyle = { ...styles.row, gridTemplateColumns: gridCols(isNarrow, '1fr 1fr') };
  const [form, setForm] = useState({ ...EMPTY_FORM });
  // A11YLABEL: one id prefix per component INSTANCE, so two copies of this
  // form on one page cannot produce duplicate ids.
  const a11yId = useId();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [allEmployees, setAllEmployees] = useState([]);
  const [managerSearch, setManagerSearch] = useState('');
  const [managerName, setManagerName] = useState('');
  const [showManagerList, setShowManagerList] = useState(false);
  // MGRSEARCH: true only when the manager-search fetch itself failed - distinct from "no
  // employees yet", so the field can show a visible, non-alarming notice instead of silently
  // staying empty forever (the previous catch(() => {}) discarded the error entirely).
  const [managerSearchUnavailable, setManagerSearchUnavailable] = useState(false);
  const managerRef = useRef(null);

  // DELEGATELOCFILTER: a non-privileged account (currentUserRole neither 'coo' nor 'admin' - the
  // two mounts for a can_submit_new_starters delegate, App.js's payroll/finance shells and
  // EmployeeView.js's own-profile shell, never pass currentUserRole at all, matching this
  // codebase's existing convention for that prop, e.g. the CERTFN classification fields above)
  // can only ever successfully submit for their OWN entity - employees.py:583-597's backend gate
  // already enforces this, unmodified. ownLocations is the delegate's own allowed Location(s),
  // resolved via ENTITY_TO_LOCATIONS from their own Employee.primary_entity; EMPTY for a privileged
  // caller (never fetched) or while the fetch is pending/unavailable, in which case the Location
  // dropdown correctly falls back to its normal unfiltered behaviour rather than blocking on it.
  // SENEGALLOC: this is now an ARRAY - usually one location, but a CIV delegate gets two (Côte
  // d'Ivoire + Senegal). One-element arrays reproduce the old single-locked behaviour exactly.
  const isPrivileged = currentUserRole === 'coo' || currentUserRole === 'admin';
  // HRS3 (confirmed COO decision): hr creates new starters for EVERY entity, so it is not narrowed
  // to its own entity's locations the way a delegate is. The server already admits hr for any
  // entity (_caller_may_create in employees.py). This is deliberately NOT isPrivileged: that flag
  // also shows the contract exemption, which stays coo/admin only on both sides.
  const mayPickAnyEntity = isPrivileged || currentUserRole === 'hr';
  const [ownLocations, setOwnLocations] = useState([]);

  // ADDEMPDRAFT: save-as-draft. Unlike the New Starter, Leaver and Sponsor forms, which hang a
  // draft off an existing form row, Add Employee is the form that CREATES the employee, so there
  // is no row to attach to. Drafts therefore live in their own employee_drafts table, holding the
  // payload as JSON with no foreign key to employees and nothing written to company_email - so an
  // open draft reserves no address and the duplicate-email check still runs for the first time at
  // real submission.
  const [draftId, setDraftId] = useState(null);      // the draft currently being edited, if any
  const [drafts, setDrafts] = useState([]);          // this account's own drafts (author-scoped)
  const [draftBusy, setDraftBusy] = useState(false);
  const [draftNote, setDraftNote] = useState(null);
  const [showDrafts, setShowDrafts] = useState(false);

  const loadDrafts = () => {
    axios.get(`${API}/employees/drafts`)
      .then(res => setDrafts(res.data || []))
      // Fails soft: an account that cannot draft simply sees no drafts affordance, rather than an
      // error on a form that otherwise works perfectly.
      .catch(() => setDrafts([]));
  };
  useEffect(() => { loadDrafts(); }, []); // eslint-disable-line

  const saveDraft = async () => {
    setDraftBusy(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/employees/drafts`, { id: draftId, payload: form });
      setDraftId(res.data.id);
      setDraftNote('Draft saved.');
      loadDrafts();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not save draft.');
    }
    setDraftBusy(false);
  };

  const resumeDraft = async (id) => {
    setDraftBusy(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/employees/drafts/${id}`);
      // Merge onto EMPTY_FORM so a draft saved before a field existed (or after one was removed)
      // still loads, with anything unknown simply absent rather than breaking the form.
      setForm({ ...EMPTY_FORM, ...(res.data.payload || {}) });
      setDraftId(res.data.id);
      setManagerName('');
      setManagerSearch('');
      setShowDrafts(false);
      setDraftNote('Draft resumed.');
    } catch (err) {
      setError('Could not open that draft.');
    }
    setDraftBusy(false);
  };

  const discardDraft = async (id) => {
    setDraftBusy(true);
    setError(null);
    try {
      await axios.delete(`${API}/employees/drafts/${id}`);
      if (id === draftId) { setDraftId(null); setForm({ ...EMPTY_FORM }); }
      setDraftNote('Draft discarded.');
      loadDrafts();
    } catch (err) {
      setError('Could not discard that draft.');
    }
    setDraftBusy(false);
  };

  useEffect(() => {
    axios.get(`${API}/employees/manager-search`)
      .then(res => setAllEmployees(res.data))
      .catch(() => setManagerSearchUnavailable(true));
  }, []);

  useEffect(() => {
    // HRS3: hr is not entity-narrowed, so ownLocations stays empty and the full list renders.
    if (mayPickAnyEntity) return;
    axios.get(`${API}/employees/me`)
      .then(res => {
        const locs = ENTITY_TO_LOCATIONS[res.data?.primary_entity];
        if (locs && locs.length) setOwnLocations(locs);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SENEGALLOC: pre-select and lock the Location (and cascading Company) ONLY when the delegate has
  // exactly ONE allowed location (the common case - behaviour byte-identical to before). A delegate
  // with MULTIPLE allowed locations (a CIV delegate: Côte d'Ivoire + Senegal) must choose between
  // them, so nothing is auto-selected or locked for them - they pick from the restricted list.
  // A fresh mount starts with EMPTY_FORM's blank location and the fetch above resolves after first
  // render, so this applies the value the moment it arrives; guarded on form.location being empty so
  // it never clobbers a value the user (or DEMO fill) already set, and never re-fires once applied.
  useEffect(() => {
    if (ownLocations.length === 1 && !form.location) {
      setForm(prev => ({ ...prev, location: ownLocations[0] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownLocations]);

  // COMPANYLOCK: whenever the selected location has exactly ONE real company (ignoring "Other"),
  // pin Company to it so the locked/greyed field always shows the correct value and submit passes -
  // covers every entry path into form.location (manual pick, DEMO fill, and the SENEGALLOC delegate
  // pre-select above, which sets location without touching company). Never overrides a
  // multi-company location (South Africa keeps whatever the user chose); only assigns when the
  // value would actually change, so it never loops. Blank location clears nothing here (the
  // Location onChange already resets company on a location change).
  useEffect(() => {
    const real = realCompaniesFor(form.location);
    if (real.length === 1 && form.company !== real[0]) {
      setForm(prev => ({ ...prev, company: real[0] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.location]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (managerRef.current && !managerRef.current.contains(e.target)) {
        setShowManagerList(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredManagers = managerSearch.trim().length < 1 ? [] : allEmployees.filter(emp => {
    const full = `${emp.first_name} ${emp.last_name}`.toLowerCase();
    return full.includes(managerSearch.toLowerCase());
  }).slice(0, 8);

  const fillIfEmpty = (field, value) => {
    setForm(prev => ({ ...prev, [field]: prev[field] ? prev[field] : value }));
  };

  // EMAILAUTOFILL: normalize one name part for a company_email local segment. Strips accents
  // (NFD decomposition then drops the combining diacritical marks), lowercases, and removes every
  // character that is not a-z or 0-9 - so spaces, hyphens and apostrophes inside a part collapse
  // away. Examples: "Marie-Lynne" -> "marielynne", "Agbaholou" -> "agbaholou", "Côte" -> "cote",
  // "Van Der Berg" -> "vanderberg". This keeps the address a single valid local part with exactly
  // one dot between first and last (firstname.lastname), matching EMAILLOWER's lowercase rule.
  const normalizeNamePart = (raw) =>
    (raw || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')  // drop combining accents (the NFD-separated diacritics)
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');

  // EMAILAUTOFILL: the auto-fill address derived from a given first/last name (empty string if
  // either part normalizes to nothing, so we never produce ".@..." or "name.@...").
  const autoEmailFor = (first, last) => {
    const f = normalizeNamePart(first);
    const l = normalizeNamePart(last);
    return (f && l) ? `${f}.${l}@enkocapital.com` : '';
  };

  // EMAILAUTOFILL: recompute company_email from the (about-to-change) name, but ONLY when the
  // current email is either empty OR still exactly the value auto-derived from the PREVIOUS name -
  // i.e. the user has not manually diverged. A manual edit (email != auto-value for the old name)
  // is preserved and never overwritten, even if the name changes again afterwards. This is the
  // exact "don't clobber a manual edit" rule: the comparison against the old-name auto-value is
  // what distinguishes a still-suggested field from a hand-edited one, with no extra flag/ref.
  const applyNameAndMaybeEmail = (patch) => {
    setForm(prev => {
      const next = { ...prev, ...patch };
      const prevAuto = autoEmailFor(prev.first_name, prev.last_name);
      const emailIsStillSuggested = prev.company_email === '' || prev.company_email === prevAuto;
      if (emailIsStillSuggested) {
        next.company_email = autoEmailFor(next.first_name, next.last_name) || prev.company_email;
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!form.first_name)                  { setError('First name is required.');                    return; }
    if (!form.last_name)                   { setError('Last name is required.');                     return; }
    if (!form.company_email)               { setError('Company email is required.');                 return; }
    if (!form.existing_employee && !form.mobile) { setError('Mobile number is required.');           return; }
    // PERSONALREQ: required for NEW STARTERS only, matching the mobile/gender/employee_type
    // pattern above. WELCOMEDELAY corrected the REASON, not the rule: this used to say the
    // personal address was the welcome email's only deliverable channel, which stopped being
    // true when the welcome moved to company-only, five hours after Section 2. It is still
    // required, for payslips and as the way to reach someone whose company mailbox is wrong.
    // An existing employee being migrated already has a working mailbox, so it stays optional
    // for them.
    if (!form.existing_employee && !(form.personal_email || '').trim()) {
      setError('Personal email is required. It is the address for payslips, and the way to reach a new starter if their company mailbox is wrong.');
      return;
    }
    // EMAILFORMAT: PERSONALREQ above only checks presence, not format - mirrors the backend's
    // own _is_valid_email_format check (employees.py) so a malformed value is caught here too,
    // not only after a round trip to the server. Checked only when a value is present, so this
    // never fires for a blank field either flow already allows.
    if ((form.personal_email || '').trim() && !EMAIL_FORMAT_RE.test(form.personal_email.trim())) {
      setError('Personal email is not a valid email address.');
      return;
    }
    if (!form.location)                    { setError('Location is required.');                      return; }
    if (!form.company)                     { setError('Company is required.');                       return; }
    if (!form.job_title)                   { setError('Job title is required.');                     return; }
    if (!form.department)                  { setError('Department is required.');                    return; }
    if (!form.start_date)                  { setError(startDateRequired);                            return; }
    // GENDEREXIST: Gender is now required for BOTH paths (see the field render below). Type stays
    // new-starter-only. Mobile Number (line above) is DELIBERATELY unchanged - separate decision.
    if (!form.gender) { setError('Gender is required.');                  return; }
    if (!form.existing_employee && !form.employee_type) { setError('Type is required.');             return; }
    // INTERNENDREQ (confirmed COO decision): an intern must have an end date at creation -
    // without it the offboarding chain (INTERNOFFBOARD task, ended badge, offboarding CTA)
    // never fires. Mirrors the server-side 400 in create_employee.
    if (form.employee_type === 'intern' && !form.internship_end_date) {
      setError('Internship end date is required for interns.');
      return;
    }
    if (currentUserRole === 'coo') {
      const hasClassification = form.is_certified_function || form.is_non_certified_function || form.is_senior_management;
      if (!hasClassification && !form.classification_none_confirmed) {
        setError('Select at least one classification (Certified Function, Non-Certified Function, or Senior Management), or explicitly confirm "None of these apply".');
        return;
      }
    }
    setLoading(true);
    setError(null);
    try {
      const payload = { ...form };
      if (!payload.manager_id) payload.manager_id = null;
      if (!payload.secondary_entity) payload.secondary_entity = null;
      // ADDEMPPERSONAL: optional field, so send NULL rather than an empty string when left blank.
      // resolve_welcome_email treats "" and None the same (it strips before testing), but storing
      // NULL keeps this column consistent with how EmployeeProfile.js's editor saves it.
      if (!payload.personal_email || !payload.personal_email.trim()) payload.personal_email = null;
      // INTERNEND: a Date field - an empty string is not a valid date for the backend schema, so
      // send NULL when blank (also naturally null for any non-intern, whose picker never renders).
      if (!payload.internship_end_date) payload.internship_end_date = null;
      if (createdBy) payload.created_by = createdBy;
      // ADDEMPDRAFT: tells the server which draft this submission came from, so it can be cleared
      // once the employee is genuinely created. Sent only when this form IS a resumed draft. On a
      // FAILED submission the server never reaches its cleanup, so the draft survives and the work
      // is not lost - see the catch below, which deliberately leaves draftId set.
      if (draftId) payload.draft_id = draftId;
      await axios.post(`${API}/employees/`, payload);
      // The server has deleted the draft; drop the local handle and refresh the list.
      if (draftId) { setDraftId(null); loadDrafts(); }
      if (inline) {
        setSuccess(`${form.first_name} ${form.last_name} has been added successfully.`);
        setForm({ ...EMPTY_FORM });
        setManagerName('');
        setManagerSearch('');
      } else {
        onCreated && onCreated();
        onClose && onClose();
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      // STARTDATEMAILS: the server's refusal of a new starter without a start date, translated.
      if (detail === 'Start date is required.') { setError(startDateRequired); setLoading(false); return; }
      setError(typeof detail === 'string' ? detail : JSON.stringify(detail) || 'Error creating employee');
    }
    setLoading(false);
  };

  const formContent = (
    <div style={inline ? styles.inlineCard : styles.modal}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

        <div style={styles.header}>
          <h2 style={styles.title}>Add New Employee</h2>
          <div style={styles.headerRight}>
            {isTestAccount() && <button style={styles.demoBtn} onClick={() => setForm(DEMO)}>Fill demo data</button>}
            {!inline && <button style={styles.closeBtn} onClick={onClose}>✕</button>}
          </div>
        </div>

        {/* ADDEMPDRAFT: the RESUME entry point lives at the top of the Add Employee form itself,
            deliberately rather than in a shell sidebar. The form is mounted as a modal from three
            different places (the COO Dashboard, the Country Lead / finance shell, and EmployeeView),
            so a sidebar item would have to be added three times and would drift; putting it here
            means every mount gets it automatically, and the drafts are offered at exactly the moment
            someone is about to type the same thing again. The bar only appears when this account
            actually HAS drafts, so a first-time user sees nothing new. */}
        {drafts.length > 0 && (
          <div style={styles.draftBar}>
            <div style={styles.draftBarRow}>
              <span style={styles.draftBarText}>
                {drafts.length === 1 ? 'You have 1 saved draft.' : `You have ${drafts.length} saved drafts.`}
              </span>
              <button style={styles.draftLink} onClick={() => setShowDrafts(v => !v)}>
                {showDrafts ? 'Hide' : 'Resume a draft'}
              </button>
            </div>
            {showDrafts && (
              <div style={styles.draftList}>
                {drafts.map(d => (
                  <div key={d.id} style={styles.draftRow}>
                    <span style={styles.draftLabel}>
                      {d.label || 'Untitled draft'}
                      <span style={styles.draftMeta}>
                        {d.updated_at ? ` saved ${toDisplayDate(d.updated_at).toLocaleString()}` : ''}
                      </span>
                    </span>
                    <span>
                      <button style={styles.draftLink} onClick={() => resumeDraft(d.id)} disabled={draftBusy}>Resume</button>
                      <button style={{ ...styles.draftLink, color: '#A63A3A' }} onClick={() => discardDraft(d.id)} disabled={draftBusy}>Discard</button>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {draftNote && <div style={styles.draftNote}>{draftNote}</div>}

        <div style={styles.existingToggle}>
          <label style={{ ...styles.checkboxLabel, fontSize: '14px', fontWeight: '500', color: '#1a1a2e' }}>
            <input
              type="checkbox"
              checked={form.existing_employee}
              onChange={e => setForm({ ...form, existing_employee: e.target.checked })}
              style={{ marginRight: '10px', cursor: 'pointer', width: '15px', height: '15px' }}
            />
            Existing employee (no onboarding required)
          </label>
          {form.existing_employee && (
            <p style={{ margin: '6px 0 0 25px', fontSize: '12px', color: '#666' }}>
              A portal account will be created and credentials emailed. No New Starter form will be generated.
            </p>
          )}
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <div style={rowStyle}>
          <div style={styles.field}>
            <label style={styles.label} htmlFor={`${a11yId}-first-name`}>First Name <RequiredMark /></label>
            
            <input id={`${a11yId}-first-name`} style={styles.input} value={form.first_name}
              onChange={e => applyNameAndMaybeEmail({ first_name: e.target.value })}
              onKeyDown={e => e.key === 'Enter' && fillIfEmpty('first_name', DEMO.first_name)}
              placeholder="First name" />
          </div>
          <div style={styles.field}>
            <label style={styles.label} htmlFor={`${a11yId}-last-name`}>Last Name <RequiredMark /></label>
            
            <input id={`${a11yId}-last-name`} style={styles.input} value={form.last_name}
              onChange={e => applyNameAndMaybeEmail({ last_name: e.target.value })}
              onKeyDown={e => e.key === 'Enter' && fillIfEmpty('last_name', DEMO.last_name)}
              placeholder="Last name" />
          </div>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Company Email <RequiredMark /></label>
          {/* EMAILLOWER: force company_email to lowercase live as it is typed, so it is impossible
              to submit a mixed-case address from this form. Live (not on blur/submit) so the user
              sees the normalised value immediately and the form state is always already lowercase.
              Applies identically to every user of this component - a COO or a delegate with
              can_submit_new_starters both use this same field. The backend independently lowercases
              on save too (create_employee), so this is the UX-facing half of a defence-in-depth pair. */}
          <input style={styles.input} type="email" value={form.company_email}
            onChange={e => setForm({...form, company_email: e.target.value.toLowerCase()})}
            onKeyDown={e => e.key === 'Enter' && fillIfEmpty('company_email', DEMO.company_email)}
            placeholder="Company email" />
        </div>

        {/* ADDEMPPERSONAL: captured at creation time, and still editable retroactively on
            EmployeeProfile.js. WELCOMEDELAY: this no longer has anything to do with the welcome
            email, which now goes to the company address only. It is the payslip address and the
            fallback way to reach someone whose company mailbox turns out to be wrong. */}
        <div style={styles.field}>
          <label style={styles.label} htmlFor={`${a11yId}-personal-email`}>
            Personal Email {!form.existing_employee && <RequiredMark />}
          </label>
          
          <input id={`${a11yId}-personal-email`} style={styles.input} type="email" value={form.personal_email}
            onChange={e => setForm({...form, personal_email: e.target.value})}
            onKeyDown={e => e.key === 'Enter' && fillIfEmpty('personal_email', DEMO.personal_email)}
            placeholder={form.existing_employee ? 'Personal email (optional)' : 'Personal email'} />
          <p style={{ fontSize: '12px', color: '#888', margin: '6px 0 0 0' }}>
            {form.existing_employee
              ? 'Optional for an existing employee, who already has a company mailbox.'
              : 'Required: the address for payslips, and how we reach them if their company mailbox is wrong.'}
          </p>
        </div>

        {!form.existing_employee && (
          <div style={styles.field}>
            <label style={styles.label} htmlFor={`${a11yId}-mobile-number`}>Mobile Number <RequiredMark /></label>
            
            <input id={`${a11yId}-mobile-number`} style={styles.input} type="tel" value={form.mobile}
              onChange={e => setForm({...form, mobile: e.target.value})}
              placeholder="Mobile number" />
          </div>
        )}

        <div style={rowStyle}>
          <div style={styles.field}>
            <label style={styles.label}>Location <RequiredMark /></label>
            {/* DELEGATELOCFILTER + SENEGALLOC: a non-privileged delegate sees ONLY the location(s)
                their own entity allows (ownLocations) - matching the backend's own-entity-only gate
                (employees.py:583-597, unmodified) rather than letting them pick a value that would
                only fail at submit time. With ONE allowed location it is pre-selected (by the effect
                above) and disabled - byte-identical to before. With MULTIPLE (a CIV delegate: Côte
                d'Ivoire + Senegal) the select stays ENABLED with a "Select location" placeholder so
                they can choose between them. A privileged caller (coo/admin), or a delegate whose
                own location(s) could not be resolved (fetch pending/failed/no linked record), sees
                the full unfiltered list - the exact previous behaviour. */}
            <select style={styles.input} value={form.location}
              disabled={!isPrivileged && ownLocations.length === 1}
              onChange={e => {
                const loc = e.target.value;
                const available = LOCATION_COMPANIES[loc] || [];
                setForm(prev => ({
                  ...prev,
                  location: loc,
                  company: available.includes(prev.company) ? prev.company : ''
                }));
              }}>
              {(!isPrivileged && ownLocations.length > 0) ? (
                <>
                  {/* Placeholder only when there is a genuine choice to make (multiple locations);
                      a single locked location needs none. */}
                  {ownLocations.length > 1 && <option value="">Select location</option>}
                  {ownLocations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
                </>
              ) : (
                <>
                  <option value="">Select location</option>
                  <option value="UK">UK</option>
                  <option value="Rwanda">Rwanda</option>
                  <option value="Côte d'Ivoire">Côte d'Ivoire</option>
                  <option value="Senegal">Senegal</option>
                  <option value="Mauritius">Mauritius</option>
                  <option value="South Africa">South Africa</option>
                  <option value="Cameroon">Cameroon</option>
                </>
              )}
            </select>
            {!isPrivileged && ownLocations.length > 0 && (
              <p style={{ fontSize: '11px', color: '#888', margin: '4px 0 0 0' }}>
                Restricted to your own entity.
              </p>
            )}
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Company <RequiredMark /></label>
            {/* COMPANYLOCK (confirmed COO decision): grey out and lock the Company field when the
                selected location has exactly ONE real company (every location except South Africa),
                "Other" not counting as a real second choice. The effect above has already pinned
                form.company to that single company, so the disabled select shows the right value and
                submit passes. A location with MULTIPLE real companies (South Africa's 3) stays
                selectable. No location selected yet leaves it enabled with the placeholder. */}
            {/* COOCOMPANY (confirmed decision by Marie-Lynne, relaying the COO): the lock is
                lifted for the COO ONLY. Every other role, ADMIN INCLUDED, keeps it exactly as it
                was - a Country Lead still has the field pinned to the single company of their
                location.

                Admin is deliberately NOT included: the request named the COO, admin is a separate
                account with its own holder, and the lock is a guard rail rather than a permission,
                so lifting it for a role nobody asked about would be a change made on assumption.
                Trivial to add if wanted.

                WORTH KNOWING, and reported before this was built: for five of the six locations
                the dropdown holds exactly ONE real company plus "Other", so unlocking lets the COO
                re-pick the same value or choose "Other", and nothing more. It is only materially
                different for South Africa, which was never locked anyway. Widening the list to
                companies from OTHER locations was NOT done, because a company implies an entity
                through COMPANY_TO_ENTITY while the Location field drives the delegate filtering,
                so the two would disagree - that needs an explicit decision. */}
            {(() => {
              const companyLocked = realCompaniesFor(form.location).length === 1
                && currentUserRole !== 'coo';
              return (
                <select
                  style={{ ...styles.input, ...(companyLocked ? styles.inputLocked : {}) }}
                  value={form.company}
                  disabled={companyLocked}
                  onChange={e => setForm({...form, company: e.target.value})}>
                  <option value="">Select company</option>
                  {(LOCATION_COMPANIES[form.location] || []).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              );
            })()}
          </div>
        </div>

        <div style={styles.field}>
          <label style={styles.label} htmlFor={`${a11yId}-secondary-entity-optional-only-for-e`}>Secondary Entity <span style={styles.tbcNote}>(optional - only for employees also certified/trading for a second entity)</span></label>
          
          <select id={`${a11yId}-secondary-entity-optional-only-for-e`} style={styles.input} value={form.secondary_entity}
            onChange={e => setForm({...form, secondary_entity: e.target.value})}>
            <option value="">None</option>
            {SECONDARY_ENTITY_OPTIONS.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
        </div>

        <div style={rowStyle}>
          <div style={styles.field}>
            <label style={styles.label}>Job Title <RequiredMark /></label>
            {/* GRADEFIELD: job_title reverted to FREE TEXT. The 4 fixed values
                (Partner/Analyst/Associate/Director) are a seniority GRADE, now their own strict
                Grade dropdown below - so job_title holds the ACTUAL role, which varies widely
                ("Portfolio Manager", "Compliance Officer", ...) and does not belong in a fixed list.
                (Reverts the earlier JOBTITLESTD dropdown, whose 4 options duplicated the grades.) */}
            <input style={styles.input} value={form.job_title}
              onChange={e => setForm({ ...form, job_title: e.target.value })}
              placeholder="e.g. Portfolio Manager" />
          </div>
          <div style={styles.field}>
            <label style={styles.label} htmlFor={`${a11yId}-department`}>Department <RequiredMark /></label>
            
            <select id={`${a11yId}-department`} style={styles.input} value={form.department}
              onChange={e => setForm({...form, department: e.target.value})}>
              <option value="">Select department</option>
              {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
        </div>

        {/* CONTRACTEXEMPT2 (confirmed COO decision): waiving the contract-upload requirement is a
            compliance decision - visible and settable ONLY for coo/admin (isPrivileged), never a
            delegate, matching the backend's create_employee 403 guard exactly. Defaults to
            form.contract_exempt (false in EMPTY_FORM), so a non-privileged submit always sends
            false and is never rejected - only an actual attempt to grant the exemption would be,
            and that control simply does not exist for that caller. */}
        {isPrivileged && (
          <div style={styles.field}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={form.contract_exempt}
                onChange={e => setForm({ ...form, contract_exempt: e.target.checked })}
                style={{ marginRight: '8px', cursor: 'pointer' }}
              />
              Exempt from contract upload, for partners with a deed instead of a contract
            </label>
          </div>
        )}

        <div style={styles.field} ref={managerRef}>
          <label style={styles.label}>Line Manager</label>
          <div style={{ position: 'relative' }}>
            <input
              style={styles.input}
              value={managerName || managerSearch}
              placeholder="Type a name to search…"
              onChange={e => {
                const val = e.target.value;
                setManagerSearch(val);
                setManagerName('');
                setForm(prev => ({ ...prev, manager_id: '' }));
                setShowManagerList(true);
              }}
              onFocus={() => { if (!managerName) setShowManagerList(true); }}
            />
            {managerName && (
              <button
                style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: '14px', lineHeight: 1 }}
                onClick={() => { setManagerName(''); setManagerSearch(''); setForm(prev => ({ ...prev, manager_id: '' })); setShowManagerList(false); }}
              >✕</button>
            )}
            {managerSearchUnavailable && (
              <p style={{ fontSize: '12px', color: '#8a6d00', margin: '6px 0 0' }}>
                Manager search is temporarily unavailable. You can still create the employee and set their manager later.
              </p>
            )}
            {showManagerList && filteredManagers.length > 0 && (
              <div style={styles.managerDropdown}>
                {filteredManagers.map(emp => (
                  <div
                    key={emp.id}
                    style={styles.managerOption}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#FFF8EC'; e.currentTarget.style.color = '#8B6914'; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = '#1a1a2e'; }}
                    onMouseDown={e => {
                      e.preventDefault();
                      const name = `${emp.first_name} ${emp.last_name}`;
                      setManagerName(name);
                      setManagerSearch('');
                      setForm(prev => ({ ...prev, manager_id: emp.id }));
                      setShowManagerList(false);
                    }}
                  >
                    <span style={{ fontWeight: 500 }}>{emp.first_name} {emp.last_name}</span>
                    <span style={{ fontSize: '11px', color: '#aaa', marginLeft: '8px' }}>{emp.job_title || emp.department || ''}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={styles.field}>
          <label style={styles.label} htmlFor={`${a11yId}-start-date`}>Start Date <RequiredMark /></label>
          
          <input id={`${a11yId}-start-date`} style={styles.input} type="date" value={form.start_date}
            onChange={e => setForm({...form, start_date: e.target.value})} />
        </div>

        {!inline && !form.existing_employee && (
          <div style={styles.field}>
            <label style={{ ...styles.checkboxLabel, fontSize: '13px', color: '#555' }}>
              <input
                type="checkbox"
                checked={form.can_submit_new_starters}
                onChange={e => setForm({ ...form, can_submit_new_starters: e.target.checked })}
                style={{ marginRight: '8px', cursor: 'pointer' }}
              />
              Can submit new starters for their entity
            </label>
          </div>
        )}

        {/* GENDEREXIST (confirmed gap fix): Gender renders and is REQUIRED for BOTH the new-starter
            AND the existing-employee paths (no !form.existing_employee wrapper). An existing employee
            gets no New Starter Form, no EmployeeProfile editor, and there is no backend endpoint to
            set gender later, so hiding it here made gender permanently unreachable for them. Shown
            for both paths like the Regulatory Classification block already is. Employee.gender /
            EmployeeCreate.gender already exist and are persisted for both paths - no backend change.
            Mobile Number is DELIBERATELY left as-is (still hidden for existing employees, separate
            decision). */}
        <div style={styles.field}>
          <label style={styles.label} htmlFor={`${a11yId}-gender`}>Gender <RequiredMark /></label>
          
          <select id={`${a11yId}-gender`} style={styles.input} value={form.gender}
            onChange={e => setForm({...form, gender: e.target.value})}>
            <option value="">Select</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Prefer not to say">Prefer not to say</option>
          </select>
        </div>

        {!form.existing_employee && (
          <div style={styles.field}>
            <label style={styles.label}>Type <RequiredMark /></label>
            <div style={styles.toggle}>
              {['employee', 'contractor', 'intern'].map(t => (
                <button key={t}
                  style={{
                    ...styles.toggleBtn,
                    backgroundColor: form.employee_type === t ? '#1a1a2e' : '#fff',
                    color: form.employee_type === t ? '#fff' : '#1a1a2e',
                    textTransform: 'capitalize'
                  }}
                  onClick={() => setForm({...form, employee_type: t, ...(t !== 'intern' ? { intern_term: '', intern_duration: '', internship_end_date: '' } : {})})}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </div>
        )}

        {!form.existing_employee && form.employee_type === 'intern' && (
          <div style={styles.field}>
            <label style={styles.label}>Internship term</label>
            <div style={styles.toggle}>
              {[['short_term', 'Short-term (< 2 months)'], ['long_term', 'Long-term']].map(([val, lbl]) => (
                <button key={val}
                  style={{
                    ...styles.toggleBtn,
                    backgroundColor: form.intern_term === val ? '#1a1a2e' : '#fff',
                    color: form.intern_term === val ? '#fff' : '#1a1a2e',
                  }}
                  onClick={() => setForm({...form, intern_term: val, ...(val !== 'long_term' ? { intern_duration: '' } : {})})}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
        )}

        {!form.existing_employee && form.employee_type === 'intern' && form.intern_term === 'long_term' && (
          <div style={styles.field}>
            <label style={styles.label} htmlFor={`${a11yId}-expected-duration`}>Expected duration</label>
            
            <input id={`${a11yId}-expected-duration`} style={styles.input} type="text" value={form.intern_duration}
              onChange={e => setForm({...form, intern_duration: e.target.value})}
              placeholder="e.g. 6 months" />
          </div>
        )}

        {!form.existing_employee && form.employee_type === 'intern' && (
          <div style={styles.field}>
            <label style={styles.label} htmlFor={`${a11yId}-internship-end-date`}>Internship end date <RequiredMark /></label>
            
            <input id={`${a11yId}-internship-end-date`} style={styles.input} type="date" value={form.internship_end_date}
              onChange={e => setForm({...form, internship_end_date: e.target.value})} />
          </div>
        )}

        {!form.existing_employee && (
          <div style={styles.visaField}>
            <label style={styles.label}>Requires Visa Sponsorship ?</label>
            <div style={styles.toggle}>
              <button style={{
                  ...styles.toggleBtn,
                  backgroundColor: form.requires_visa ? '#1a1a2e' : '#fff',
                  color: form.requires_visa ? '#fff' : '#1a1a2e'
                }}
                onClick={() => setForm({...form, requires_visa: true})}>
                Yes
              </button>
              <button style={{
                  ...styles.toggleBtn,
                  backgroundColor: !form.requires_visa ? '#1a1a2e' : '#fff',
                  color: !form.requires_visa ? '#fff' : '#1a1a2e'
                }}
                onClick={() => setForm({...form, requires_visa: false})}>
                No
              </button>
            </div>
          </div>
        )}

        {!form.existing_employee && form.requires_visa && (
          <div style={styles.visaNote}>
            4 tasks will be created automatically for this employee.
          </div>
        )}

        {/* Regulatory classification (CERTFN) - COO only, 3 simple top-level flags (no detail -
            Compliance, or COO temporarily, fills in the actual Certified Function(s)/
            Non-Certified Function(s) later via New Starter Form Section 4, which keeps the full
            8-item checklist/extensible list unchanged). Shown for BOTH the new-employee and
            existing-employee paths (not wrapped in !form.existing_employee), matching this
            task's explicit scope. Not shown to the canSubmitNewStarters delegate self-submission
            flow (EmployeeView.js), which doesn't pass currentUserRole - these are Compliance/COO
            regulatory determinations, not something a delegate submitter should set. */}
        {currentUserRole === 'coo' && (
          <div style={styles.field}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={form.is_certified_function}
                disabled={form.classification_none_confirmed}
                onChange={e => setForm({ ...form, is_certified_function: e.target.checked, classification_none_confirmed: false })}
                style={{ marginRight: '8px', cursor: 'pointer' }}
              />
              Certified Function
            </label>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={form.is_non_certified_function}
                disabled={form.classification_none_confirmed}
                onChange={e => setForm({ ...form, is_non_certified_function: e.target.checked, classification_none_confirmed: false })}
                style={{ marginRight: '8px', cursor: 'pointer' }}
              />
              Non-Certified Function
            </label>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={form.is_senior_management}
                disabled={form.classification_none_confirmed}
                onChange={e => setForm({ ...form, is_senior_management: e.target.checked, classification_none_confirmed: false })}
                style={{ marginRight: '8px', cursor: 'pointer' }}
              />
              Senior Management
            </label>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={form.classification_none_confirmed}
                onChange={e => setForm({
                  ...form,
                  classification_none_confirmed: e.target.checked,
                  is_certified_function: e.target.checked ? false : form.is_certified_function,
                  is_non_certified_function: e.target.checked ? false : form.is_non_certified_function,
                  is_senior_management: e.target.checked ? false : form.is_senior_management,
                })}
                style={{ marginRight: '8px', cursor: 'pointer' }}
              />
              None of these apply
            </label>
          </div>
        )}

        <div style={styles.actions}>
          {!inline && <button style={styles.cancelBtn} onClick={onClose}>Cancel</button>}
          {/* ADDEMPDRAFT: save as draft. Deliberately NOT validated - a draft is incomplete by
              definition, so the required-field checks in handleSubmit must not run here. */}
          <button style={styles.draftBtn} onClick={saveDraft} disabled={draftBusy || loading}>
            {draftBusy ? 'Saving...' : (draftId ? 'Update draft' : 'Save as draft')}
          </button>
          {/* Discard is offered for the draft currently open. The server allows its AUTHOR or a
              coo/admin; this control is the author's own path. */}
          {draftId && (
            <button style={styles.discardBtn} onClick={() => discardDraft(draftId)} disabled={draftBusy || loading}>
              Discard draft
            </button>
          )}
          <button style={styles.submitBtn} onClick={handleSubmit} disabled={loading}>
            {loading ? <><Spinner />Adding...</> : 'Add Employee'}
          </button>
        </div>

    </div>
  );

  if (inline) {
    return (
      <>
        {success && (
          <div style={styles.successBanner}>
            <span>{success}</span>
            <button style={styles.addAnotherBtn} onClick={() => setSuccess(null)}>+ Add another employee</button>
          </div>
        )}
        {!success && formContent}
      </>
    );
  }
  return (
    <div style={styles.overlay}>
      {formContent}
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: '#fff', borderRadius: '12px',
    padding: '28px', width: '520px', maxWidth: '90vw',
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
    maxHeight: '90vh', overflowY: 'auto'
  },
  inlineCard: {
    backgroundColor: '#fff', borderRadius: '10px',
    padding: '28px', border: '0.5px solid #EEE',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '12px' },
  title: { fontSize: '18px', fontWeight: '600', color: '#1a1a2e', margin: 0 },
  demoBtn: {
    backgroundColor: '#f0f4ff', color: '#1a1a2e',
    border: '1px solid #c7d4f5', borderRadius: '6px',
    padding: '6px 14px', fontSize: '13px', cursor: 'pointer', fontWeight: '500'
  },
  closeBtn: { background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#666' },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  field: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '500', color: '#333', marginBottom: '6px' },
  tbcNote: { fontSize: '11px', fontWeight: '400', color: '#aaa', marginLeft: '4px' },
  multiSelect: {
    border: '1px solid #ddd', borderRadius: '6px', padding: '10px 14px',
    backgroundColor: '#fafafa', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 0'
  },
  checkboxLabel: {
    display: 'flex', alignItems: 'center', fontSize: '14px',
    color: '#333', cursor: 'pointer', padding: '3px 0'
  },
  input: {
    width: '100%', padding: '10px 12px', border: '1px solid #ddd',
    borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box'
  },
  // COMPANYLOCK: greyed appearance for a locked single-company Company field.
  inputLocked: {
    backgroundColor: '#f2f2f2', color: '#777', cursor: 'not-allowed'
  },
  visaField: { marginBottom: '16px' },
  toggle: { display: 'flex' },
  toggleBtn: {
    padding: '10px 24px', border: '1px solid #1a1a2e',
    fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s'
  },
  visaNote: {
    backgroundColor: '#f0f4ff', border: '1px solid #c7d4f5',
    borderRadius: '6px', padding: '10px 14px',
    fontSize: '13px', color: '#333', marginBottom: '16px'
  },
  actions: { display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' },
  cancelBtn: {
    padding: '10px 20px', border: '1px solid #ddd',
    borderRadius: '8px', backgroundColor: '#fff',
    fontSize: '14px', cursor: 'pointer', color: '#333'
  },
  submitBtn: {
    padding: '10px 20px', border: 'none',
    borderRadius: '8px', backgroundColor: '#1a1a2e',
    color: '#fff', fontSize: '14px', cursor: 'pointer'
  },
  // ADDEMPDRAFT: secondary actions, deliberately quieter than the primary Add Employee button so
  // saving a draft never reads as the way to finish the form.
  draftBtn: {
    padding: '10px 20px', border: '1px solid #1a1a2e',
    borderRadius: '8px', backgroundColor: '#fff',
    color: '#1a1a2e', fontSize: '14px', cursor: 'pointer', fontWeight: 500,
  },
  discardBtn: {
    padding: '10px 20px', border: '1px solid #e4c6c6',
    borderRadius: '8px', backgroundColor: '#fff',
    color: '#A63A3A', fontSize: '14px', cursor: 'pointer',
  },
  draftBar: {
    marginBottom: '16px', padding: '10px 14px',
    backgroundColor: '#fdf8ee', border: '1px solid #efe0c0', borderRadius: '8px',
  },
  draftBarRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' },
  draftBarText: { fontSize: '13px', color: '#7a4d00' },
  draftList: { marginTop: '10px', borderTop: '1px solid #efe0c0', paddingTop: '8px' },
  draftRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    gap: '12px', padding: '4px 0', fontSize: '13px',
  },
  draftLabel: { color: '#333' },
  draftMeta: { color: '#999', fontSize: '12px' },
  draftLink: {
    background: 'none', border: 'none', color: '#1a1a2e',
    fontSize: '13px', cursor: 'pointer', fontWeight: 500, padding: '2px 6px',
  },
  draftNote: {
    marginBottom: '12px', padding: '8px 12px', borderRadius: '6px',
    backgroundColor: '#eaf3ee', color: '#2D4A3E', fontSize: '13px',
  },
  existingToggle: {
    marginBottom: '20px', padding: '12px 14px',
    backgroundColor: '#f8f9ff', border: '1px solid #c7d4f5', borderRadius: '8px',
  },
  error: {
    backgroundColor: '#fde8e8', border: '1px solid #e74c3c',
    borderRadius: '6px', padding: '10px 14px',
    fontSize: '13px', color: '#c0392b', marginBottom: '16px'
  },
  managerDropdown: {
    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
    backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '6px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 50, maxHeight: '220px', overflowY: 'auto',
  },
  managerOption: {
    padding: '9px 12px', cursor: 'pointer', fontSize: '13px',
    color: '#1a1a2e', backgroundColor: '#fff', display: 'flex', alignItems: 'baseline',
    borderBottom: '0.5px solid #f4f4f4', transition: 'background 0.1s',
  },
  successBanner: {
    backgroundColor: '#eaf3ee', border: '0.5px solid #2D4A3E',
    borderRadius: '8px', padding: '16px 20px', fontSize: '13px', color: '#2D4A3E',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px',
  },
  addAnotherBtn: {
    backgroundColor: '#2D4A3E', color: '#fff', border: 'none',
    borderRadius: '6px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', flexShrink: 0,
  },
};

export default AddEmployee;
